import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VISITOR_KEY = 'me_visitor_id';
const SESSION_KEY = 'me_session_id';

function genId() {
  // Lightweight UUID-ish; sufficient for analytics keys
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getVisitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = genId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return 'anon';
  }
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = genId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'anon-session';
  }
}

/**
 * Fires a single page-view event whenever the route changes.
 * Skips noisy/private paths and prevents duplicate fires for the same path.
 */
export default function usePageTracking() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const path = location.pathname + (location.search || '');

    // Skip auth/sensitive paths to keep analytics clean
    if (/^\/(reset-password|auth)/.test(location.pathname)) return;
    // Avoid duplicate fires for the same path (StrictMode double-mount)
    if (lastPath.current === path) return;
    lastPath.current = path;

    const payload = {
      path,
      referrer: document.referrer || null,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      screen_w: window.screen?.width || null,
      screen_h: window.screen?.height || null,
    };

    // Attach auth token if present (so we can attribute visits to logged-in users)
    const token = (() => {
      try { return localStorage.getItem('token'); } catch { return null; }
    })();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Fire-and-forget; never block UI on tracking
    axios
      .post(`${API_URL}/api/track/visit`, payload, { headers, timeout: 4000 })
      .catch(() => {});
  }, [location.pathname, location.search]);
}
